import { BackgroundJobStatus, VerificationCodeBatchListItemDTO } from "@bvhi/backend-api";
import { GridColDef } from "@bvhi/ui-base";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ActionCells } from "../../../../../components/datagrid/cells/action-cells";
import { DateCell } from "../../../../../components/datagrid/cells/date-cell";
import { PercentageCell } from "../../../../../components/datagrid/cells/percentage-cell";
import { BackgroundJobStatusChip } from "../../../supplier-customer-list/components/common/background-job-status-chip";
import { VerificationCodeLinkCell } from "../components/datagrid/verification-code-link-cell";
import { useVerificationBatchSortParams } from "./use-verification-batch-sort-params";

export const useVerificationCodesDataGridDefinitions = (
  handleDelete: (userId: string) => void,
  handleDownload: (userId: string) => void
) => {
  const { t } = useTranslation(["customers", "verification-codes"]);
  const { isFieldSortable } = useVerificationBatchSortParams();

  const columns: GridColDef<VerificationCodeBatchListItemDTO>[] = useMemo(() => {
    return [
      {
        field: "name",
        headerName: t("customers:customers.datagrid.header.name"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("name"),
        renderCell: (params) => <VerificationCodeLinkCell label={params.row.name} verificationCodeId={params.row.id} />,
      },
      {
        field: "backgroundJobInfo",
        headerName: t("verification-codes:verification.codes.datagrid.header.backgroundJobInfo"),
        minWidth: 150,
        width: 175,
        maxWidth: 250,
        sortable: isFieldSortable("backgroundJobInfo"),
        renderCell: (params) => <BackgroundJobStatusChip status={params.row.backgroundJobInfo?.status ?? ""} />,
      },
      {
        field: "createdAt",
        headerName: t("customers:customers.datagrid.header.createdAt"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("createdAt"),
        renderCell: (params) => <DateCell date={params.row.createdAt} />,
      },
      {
        field: "validUntil",
        headerName: t("customers:customers.datagrid.header.validUntil"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("validUntil"),
        renderCell: (params) => <DateCell date={params.row.validUntil} />,
      },
      {
        field: "used",
        headerName: t("customers:customers.datagrid.header.used"),
        minWidth: 150,
        width: 175,
        maxWidth: 250,
        sortable: isFieldSortable("used"),
        renderCell: (params) => <PercentageCell elements={params.row.used} total={params.row.created} />,
      },
      {
        field: "id",
        headerName: t("customers:customers.datagrid.header.actions"),
        flex: 1,
        minWidth: 125,
        headerClassName: "datagrid-sticky-right",
        cellClassName: "datagrid-sticky-right",
        align: "right",
        headerAlign: "right",
        sortable: isFieldSortable("id"),
        resizable: false,
        renderCell: (params) => (
          <ActionCells
            deletePermission="verification-code:batch:delete"
            detailParams={{ verificationCodeId: params.row.id }}
            detailPermission="verification-code:batch:read"
            detailRoute="/app/verification-codes/$verificationCodeId"
            downloadActionDisabled={params.row.backgroundJobInfo?.status !== BackgroundJobStatus.FINISHED}
            downloadPermission="verification-code:batch:download"
            handleDelete={handleDelete}
            handleDownload={handleDownload}
            id={params.row.id}
            showDeleteAction={true}
            showDetailAction={true}
            showDownloadAction={true}
          />
        ),
      },
    ];
  }, [t, isFieldSortable, handleDelete, handleDownload]);

  return { columns };
};
